import { useQuery } from '@tanstack/react-query'
import { stats, UserStats, SteamGame, XboxGame } from '@/lib/api'
import { FortniteShowcase } from '@/components/FortniteShowcase'
import { GamingHeatmap } from '@/components/GamingHeatmap'
import { SteamIcon, FortniteIcon, XboxIcon } from '@/components/PlatformIcons'
import { getSubdomain } from '@/lib/subdomain'

function calcLevel(xp: number): { level: number; current: number; needed: number; progress: number } {
  let level = 1
  let remaining = xp
  while (remaining >= level * 100) {
    remaining -= level * 100
    level++
  }
  const needed = level * 100
  return { level, current: remaining, needed, progress: (remaining / needed) * 100 }
}

function calcXP(data: UserStats): number {
  let xp = 0
  const steamGames = data.steam?.games || []
  const totalHours = Math.round(steamGames.reduce((s, g) => s + g.playtimeForever, 0) / 60)
  xp += steamGames.length * 5
  xp += totalHours * 2
  xp += (data.xbox?.gamerscore || 0)
  xp += (data.fortnite?.stats?.overall?.wins || 0) * 50
  xp += (data.fortnite?.stats?.overall?.kills || 0)
  xp += (data.xbox?.recentGames?.length || 0) * 10
  return xp
}

function getCombinedStats(data: UserStats) {
  const steamGames = data.steam?.games || []
  const xboxGames = data.xbox?.recentGames || []
  const totalGames = steamGames.length + xboxGames.length
  const totalHours = Math.round(
    steamGames.reduce((s, g) => s + g.playtimeForever, 0) / 60 +
    Math.round((data.fortnite?.stats?.overall?.minutesPlayed || 0) / 60)
  )
  const gamerscore = data.xbox?.gamerscore || 0
  const fortniteWins = data.fortnite?.stats?.overall?.wins || 0
  const mastered = xboxGames.filter(g => g.maxGamerscore > 0 && g.gamerscore >= g.maxGamerscore).length
  return { totalGames, totalHours, gamerscore, fortniteWins, mastered }
}

export function HomePage() {
  const subdomain = getSubdomain()
  const isLocal = subdomain === 'localhost'
  const statsLink = isLocal ? '/?subdomain=stats' : 'https://stats.n0n3.com'
  const liveLink = isLocal ? '/?subdomain=live' : 'https://live.n0n3.com'
  const libraryLink = isLocal ? '/?subdomain=stats&path=/library' : 'https://stats.n0n3.com/library'

  const { data: allStats, isLoading } = useQuery({
    queryKey: ['public', 'stats'],
    queryFn: async () => {
      const result = await stats.getAll()
      return result.data
    },
  })

  const combined = allStats ? getCombinedStats(allStats) : null
  const xp = allStats ? calcXP(allStats) : 0
  const levelInfo = calcLevel(xp)

  const connectedPlatforms = [
    allStats?.steam && { key: 'steam', name: 'Steam', icon: SteamIcon, color: '#66c0f4' },
    allStats?.fortnite && { key: 'fortnite', name: 'Fortnite', icon: FortniteIcon, color: '#9d4dbb' },
    allStats?.xbox && { key: 'xbox', name: 'Xbox', icon: XboxIcon, color: '#107c10' },
  ].filter(Boolean) as { key: string; name: string; icon: (props: any) => JSX.Element; color: string }[]

  const topSteamGames = allStats?.steam?.games
    ? [...allStats.steam.games].sort((a, b) => b.playtimeForever - a.playtimeForever).slice(0, 8)
    : []
  const maxPlaytime = topSteamGames[0]?.playtimeForever || 1

  const xboxGames = allStats?.xbox?.recentGames || []
  const xboxWithProgress = xboxGames
    .filter(g => g.maxGamerscore > 0)
    .sort((a, b) => (b.gamerscore / b.maxGamerscore) - (a.gamerscore / a.maxGamerscore))
    .slice(0, 8)

  const totalDays = combined ? Math.round(combined.totalHours / 24) : 0
  const fortniteMatches = allStats?.fortnite?.stats?.overall?.matches || 0
  const fortniteKills = allStats?.fortnite?.stats?.overall?.kills || 0
  const playersOutlived = allStats?.fortnite?.stats?.overall?.playersOutlived || 0

  return (
    <div className="min-h-[calc(100vh-8rem)]">
      {/* ── Profile Hero ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-500/10 via-gray-950 to-gray-950" />
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-[120px]" />
        <div className="absolute top-20 right-1/4 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-[120px]" />

        <div className="relative container mx-auto px-4 pt-16 pb-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-8 mb-10">
              <div className="relative flex-shrink-0">
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary-500 to-purple-600 p-1">
                  {allStats?.steam?.avatarUrl ? (
                    <img src={allStats.steam.avatarUrl} alt="n0n3" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <div className="w-full h-full rounded-full bg-gray-900 flex items-center justify-center text-5xl font-display font-bold text-gradient">n</div>
                  )}
                </div>
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-primary-600 to-purple-600 text-white text-sm font-bold px-4 py-1 rounded-full shadow-lg whitespace-nowrap">
                  LVL {levelInfo.level}
                </div>
              </div>

              <div className="text-center md:text-left flex-1">
                <h1 className="text-5xl md:text-6xl font-display font-bold mb-2">
                  <span className="text-gradient">n0n3</span>
                </h1>
                <p className="text-gray-400 text-lg mb-4">Tracking my gaming journey across platforms</p>
                <div className="flex flex-wrap justify-center md:justify-start gap-2">
                  {connectedPlatforms.map((p) => (
                    <span key={p.key} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border" style={{ borderColor: `${p.color}40`, backgroundColor: `${p.color}15`, color: p.color }}>
                      <p.icon size={14} />{p.name}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <a href={statsLink} className="btn bg-gradient-to-r from-primary-600 to-purple-600 hover:from-primary-500 hover:to-purple-500 px-6 py-3 text-center glow">View Stats</a>
                <a href={liveLink} className="btn bg-gray-800 hover:bg-gray-700 border border-gray-700 px-6 py-3 text-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse inline-block mr-2" />Live Activity
                </a>
              </div>
            </div>

            <div className="max-w-2xl mx-auto md:mx-0 mb-10">
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="text-gray-400">Level {levelInfo.level}</span>
                <span className="text-gray-500">{levelInfo.current.toLocaleString()} / {levelInfo.needed.toLocaleString()} XP</span>
              </div>
              <div className="h-2.5 bg-gray-800 rounded-full overflow-hidden border border-gray-700">
                <div className="h-full bg-gradient-to-r from-primary-500 to-purple-500 rounded-full transition-all duration-1000" style={{ width: `${levelInfo.progress}%` }} />
              </div>
              <p className="text-xs text-gray-600 mt-1">{xp.toLocaleString()} total XP</p>
            </div>

            {combined && !isLoading && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                <BigStat value={combined.totalGames} label="Games Owned" />
                <BigStat value={`${combined.totalHours.toLocaleString()}h`} label="Hours Played" />
                <BigStat value={combined.gamerscore.toLocaleString()} label="Gamerscore" />
                <BigStat value={combined.fortniteWins} label="Fortnite Wins" />
                <BigStat value={combined.mastered} label="Games Mastered" />
              </div>
            )}
            {isLoading && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {[...Array(5)].map((_, i) => <div key={i} className="bg-gray-800/50 rounded-xl p-5 border border-gray-800 animate-pulse"><div className="h-8 w-16 bg-gray-700 rounded mb-2" /><div className="h-4 w-20 bg-gray-700 rounded" /></div>)}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Platform Breakdown with Recently Played ── */}
      {allStats && (
        <section className="py-12 px-4">
          <div className="container mx-auto max-w-5xl">
            <h2 className="text-2xl font-display font-bold mb-6">Platforms</h2>
            <div className="space-y-4">
              {/* Steam */}
              {allStats.steam && (
                <PlatformRow
                  name="Steam"
                  color="#66c0f4"
                  icon={SteamIcon}
                  statItems={[
                    { label: 'games owned', value: `${allStats.steam.games?.length || 0}` },
                    { label: 'hours played', value: `${Math.round((allStats.steam.games || []).reduce((s, g) => s + g.playtimeForever, 0) / 60).toLocaleString()}` },
                  ]}
                  recentGames={(allStats.steam.recentlyPlayed || []).slice(0, 5).map(g => ({
                    name: g.name,
                    imageUrl: `https://cdn.cloudflare.steamstatic.com/steam/apps/${g.appId}/header.jpg`,
                  }))}
                  link={`${statsLink}/steam`}
                />
              )}

              {/* Fortnite */}
              {allStats.fortnite && (
                <PlatformRow
                  name="Fortnite"
                  color="#9d4dbb"
                  icon={FortniteIcon}
                  statItems={[
                    { label: 'wins', value: `${allStats.fortnite.stats?.overall?.wins || 0}` },
                    { label: 'K/D', value: (allStats.fortnite.stats?.overall?.kd || 0).toFixed(2) },
                    { label: 'kills', value: `${(allStats.fortnite.stats?.overall?.kills || 0).toLocaleString()}` },
                  ]}
                  recentGames={[]}
                  link={`${statsLink}/fortnite`}
                />
              )}

              {/* Xbox */}
              {allStats.xbox && (
                <PlatformRow
                  name="Xbox"
                  color="#107c10"
                  icon={XboxIcon}
                  statItems={[
                    { label: 'gamerscore', value: (allStats.xbox.gamerscore || 0).toLocaleString() },
                    { label: 'games', value: `${allStats.xbox.recentGames?.length || 0}` },
                  ]}
                  recentGames={(allStats.xbox.recentGames || []).slice(0, 5).map(g => ({
                    name: g.name,
                    imageUrl: g.imageUrl,
                  }))}
                  link={`${statsLink}/xbox`}
                />
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── Top Games by Playtime ── */}
      {topSteamGames.length > 0 && (
        <section className="py-12 px-4 bg-gray-900/50">
          <div className="container mx-auto max-w-5xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-display font-bold">Top Games</h2>
              <a href={libraryLink} className="text-primary-400 hover:text-primary-300 text-sm font-medium">View library →</a>
            </div>
            <div className="space-y-3">
              {topSteamGames.map((game, i) => {
                const hours = Math.round(game.playtimeForever / 60)
                const pct = (game.playtimeForever / maxPlaytime) * 100
                return (
                  <div key={game.appId} className="group flex items-center gap-4 p-3 rounded-xl bg-gray-800/50 border border-gray-800 hover:border-[#66c0f4]/30 transition-colors">
                    <span className="text-gray-600 font-bold text-sm w-6 text-right">#{i + 1}</span>
                    <img
                      src={`https://cdn.cloudflare.steamstatic.com/steam/apps/${game.appId}/header.jpg`}
                      alt="" className="w-20 h-10 object-cover rounded"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold truncate group-hover:text-[#66c0f4] transition-colors">{game.name}</span>
                        <span className="text-sm font-bold text-[#66c0f4] ml-2 flex-shrink-0">{hours.toLocaleString()}h</span>
                      </div>
                      <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#1b2838] to-[#66c0f4] rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Fortnite Showcase ── */}
      {allStats?.fortnite && <FortniteShowcase />}

      {/* ── Xbox Achievement Progress ── */}
      {xboxWithProgress.length > 0 && (
        <section className="py-12 px-4">
          <div className="container mx-auto max-w-5xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-display font-bold">Achievement Progress</h2>
              <span className="text-sm text-gray-500">{allStats?.xbox?.gamerscore?.toLocaleString()}G total</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {xboxWithProgress.map((game) => {
                const pct = Math.round((game.gamerscore / game.maxGamerscore) * 100)
                return (
                  <div key={game.titleId} className="flex items-center gap-4 p-3 rounded-xl bg-gray-800/50 border border-gray-800 hover:border-[#107c10]/30 transition-colors">
                    {game.imageUrl ? (
                      <img src={game.imageUrl} alt="" className="w-14 h-14 rounded-lg object-cover bg-gray-700" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-[#107c10]/20 flex items-center justify-center text-2xl">🎮</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold truncate">{game.name}</span>
                        <span className={`text-xs font-bold ml-2 flex-shrink-0 px-2 py-0.5 rounded-full ${pct === 100 ? 'bg-[#107c10] text-white' : 'text-gray-400'}`}>
                          {pct === 100 ? '100%' : `${game.gamerscore}/${game.maxGamerscore}G`}
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-700 ${pct === 100 ? 'bg-[#7fff00]' : 'bg-gradient-to-r from-[#107c10] to-[#7fff00]'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Recently Played ── */}
      <RecentlyPlayedSection allStats={allStats} libraryLink={libraryLink} />

      {/* ── Fun Stats ── */}
      {combined && (
        <section className="py-12 px-4">
          <div className="container mx-auto max-w-5xl">
            <h2 className="text-2xl font-display font-bold mb-6">By the Numbers</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <FunStat
                icon="📅"
                value={`${totalDays}`}
                label="Days of Gaming"
                sub={`${combined.totalHours.toLocaleString()} hours total`}
                gradient="from-blue-500/10 to-cyan-500/10"
              />
              <FunStat
                icon="🎯"
                value={fortniteMatches.toLocaleString()}
                label="BR Matches"
                sub={`${fortniteKills.toLocaleString()} eliminations`}
                gradient="from-purple-500/10 to-pink-500/10"
              />
              <FunStat
                icon="🛡️"
                value={playersOutlived.toLocaleString()}
                label="Players Outlived"
                sub={fortniteMatches > 0 ? `~${Math.round(playersOutlived / fortniteMatches)} per match` : ''}
                gradient="from-orange-500/10 to-red-500/10"
              />
              <FunStat
                icon="🏆"
                value={combined.gamerscore.toLocaleString()}
                label="Xbox Gamerscore"
                sub={`${xboxGames.length} games tracked`}
                gradient="from-green-500/10 to-emerald-500/10"
              />
            </div>
          </div>
        </section>
      )}

      {/* ── Gaming Activity Heatmap ── */}
      <GamingHeatmap />
    </div>
  )
}

// ─── Sub-components ──────────────────────────────────────────

function BigStat({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-800 hover:border-primary-500/30 transition-colors text-center">
      <div className="text-2xl md:text-3xl font-bold font-display text-white">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <div className="text-xs text-gray-500 mt-1 uppercase tracking-wider">{label}</div>
    </div>
  )
}

function PlatformRow({
  name, color, icon: Icon, statItems, recentGames, link,
}: {
  name: string
  color: string
  icon: (props: any) => JSX.Element
  statItems: { label: string; value: string }[]
  recentGames: { name: string; imageUrl?: string }[]
  link: string
}) {
  return (
    <a href={link} className="group block rounded-xl border border-gray-800 hover:border-opacity-100 transition-all overflow-hidden" style={{ borderColor: `${color}25` }}>
      <div className="h-1" style={{ backgroundColor: color }} />
      <div className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {/* Platform name + stats */}
          <div className="flex items-center gap-3 sm:min-w-[200px]">
            <span style={{ color }}><Icon size={18} /></span>
            <span className="font-bold text-lg">{name}</span>
          </div>

          <div className="flex gap-5 flex-shrink-0">
            {statItems.map((s) => (
              <div key={s.label}>
                <span className="text-xl font-bold" style={{ color }}>{s.value}</span>
                <span className="text-xs text-gray-500 ml-1.5">{s.label}</span>
              </div>
            ))}
          </div>

          {/* Recently played thumbnails */}
          {recentGames.length > 0 && (
            <div className="flex gap-1.5 ml-auto">
              {recentGames.map((g, i) => (
                <div key={i} className="relative group/thumb" title={g.name}>
                  {g.imageUrl ? (
                    <img
                      src={g.imageUrl} alt=""
                      className="w-16 h-8 object-cover rounded border border-gray-700 group-hover/thumb:border-gray-500 transition-colors"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                    />
                  ) : (
                    <div className="w-16 h-8 rounded border border-gray-700 bg-gray-800 flex items-center justify-center text-[8px] text-gray-500 truncate px-1">{g.name}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          <svg className="w-5 h-5 text-gray-600 group-hover:text-gray-300 transition-colors flex-shrink-0 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </a>
  )
}

function FunStat({ icon, value, label, sub, gradient }: { icon: string; value: string; label: string; sub: string; gradient: string }) {
  return (
    <div className={`rounded-xl p-5 bg-gradient-to-br ${gradient} border border-gray-800 text-center`}>
      <div className="text-3xl mb-2">{icon}</div>
      <div className="text-2xl font-bold font-display text-white">{value}</div>
      <div className="text-sm text-gray-400 mt-0.5">{label}</div>
      {sub && <div className="text-xs text-gray-600 mt-1">{sub}</div>}
    </div>
  )
}

function RecentlyPlayedSection({ allStats, libraryLink }: { allStats: UserStats | undefined; libraryLink: string }) {
  const recentGames: Array<{
    id: string; name: string; platform: 'steam' | 'xbox'
    imageUrl?: string; lastPlayed: Date; detail: string; platformColor: string
  }> = []

  if (allStats?.steam?.recentlyPlayed) {
    allStats.steam.recentlyPlayed.forEach((game: SteamGame) => {
      recentGames.push({
        id: `steam-${game.appId}`, name: game.name, platform: 'steam',
        imageUrl: `https://cdn.cloudflare.steamstatic.com/steam/apps/${game.appId}/header.jpg`,
        lastPlayed: new Date(), detail: `${Math.round((game.playtimeRecent || 0) / 60)}h recently`,
        platformColor: '#66c0f4',
      })
    })
  }

  if (allStats?.xbox?.recentGames) {
    allStats.xbox.recentGames.forEach((game: XboxGame) => {
      recentGames.push({
        id: `xbox-${game.titleId}`, name: game.name, platform: 'xbox',
        imageUrl: game.imageUrl, lastPlayed: new Date(game.lastPlayed || Date.now()),
        detail: `${game.gamerscore || 0}G`, platformColor: '#107c10',
      })
    })
  }

  recentGames.sort((a, b) => b.lastPlayed.getTime() - a.lastPlayed.getTime())
  if (recentGames.length === 0) return null

  return (
    <section className="py-12 px-4 bg-gray-900/50">
      <div className="container mx-auto max-w-5xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-display font-bold">Recently Played</h2>
          <a href={libraryLink} className="text-primary-400 hover:text-primary-300 text-sm font-medium">View library →</a>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {recentGames.slice(0, 8).map((game) => (
            <div key={game.id} className="group relative overflow-hidden rounded-xl border border-gray-800 hover:border-gray-600 transition-all hover:-translate-y-1 bg-gray-900">
              <div className="relative h-24 overflow-hidden bg-gray-800">
                {game.imageUrl && (
                  <img src={game.imageUrl} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent" />
                <span className="absolute top-2 right-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ backgroundColor: `${game.platformColor}30`, color: game.platformColor }}>
                  {game.platform}
                </span>
              </div>
              <div className="p-3">
                <h3 className="font-semibold text-sm truncate mb-1">{game.name}</h3>
                <span className="text-xs text-gray-500">{game.detail}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

